import React, { useMemo, useCallback } from 'react'
import { ListItem, ListItemText, Grid, Card, ListItemIcon, Box, Stack, ListItemButton, CircularProgress } from '@mui/material'
import { HHFormCheckboxField } from 'components/form-fields/v5'
import { useFormContext } from 'react-hook-form'
import { shallowEqual, useSelector } from 'react-redux'
import {
  selectRoutesMetaDataByDay,
  selectRoutesByDayLoading,
  selectSchedulingResultsMeta,
  SCHEDULING_MAP_SURFACE,
} from 'slices/configuredService/configuredServiceScheduleSlice'
import { sumBy } from 'lodash'
import HHDisplayMoney from 'components/common/HHDisplayMoney'

const weekDays = [
  { day: 'Sunday', color: '#F97316' },
  { day: 'Monday', color: '#E879F9' },
  { day: 'Tuesday', color: '#A78BFA' },
  { day: 'Wednesday', color: '#34D399' },
  { day: 'Thursday', color: '#FB7185' },
  { day: 'Friday', color: '#FBBF24' },
  { day: 'Saturday', color: '#60A5FA' },
]

const DaysOfWeekLegends = () => {
  // Variables/state
  const { control, setValue, watch } = useFormContext()
  const routesMetaDataByDay = useSelector(selectRoutesMetaDataByDay, shallowEqual)
  const isLoading = useSelector(selectRoutesByDayLoading)
  const { truncated } = useSelector(state => selectSchedulingResultsMeta(state, SCHEDULING_MAP_SURFACE.NEW_SERVICE_SCHEDULE), shallowEqual)

  // Derived values
  const totalServices = useMemo(() => {
    return Object.entries(routesMetaDataByDay).reduce((total, [day, routes]) => {
      if (day !== 'All' && Array.isArray(routes)) {
        return total + parseInt(sumBy(routes, ({ serviceCount }) => parseInt(serviceCount)))
      }
      return total
    }, 0)
  }, [routesMetaDataByDay])

  const getServiceCountForDay = useCallback(
    day => {
      const routes = routesMetaDataByDay[day] || []
      return Array.isArray(routes) ? sumBy(routes, ({ serviceCount }) => parseInt(serviceCount)) : 0
    },
    [routesMetaDataByDay]
  )

  // Handlers
  const handleToggleFactory = useCallback(
    dayLabel => () => {
      setValue(`selectedDays.${dayLabel}`, !watch(`selectedDays.${dayLabel}`))
    },
    [setValue, watch]
  )

  // Return
  return (
    <Card
      sx={{
        width: '100%',
      }}
      variant="outlined"
    >
      <Grid container>
        {/* The panel's over-capacity notice already states what's shown when the result set is
            capped; a "N services found" count next to it would contradict it. */}
        {!truncated && (
          <Grid item size={12}>
            <ListItem dense divider>
              <ListItemText
                primary={
                  <>
                    <HHDisplayMoney value={totalServices} prefix="" formatToDollars={false} decimalScale={0} />
                    <>{` services found`}</>
                  </>
                }
                secondary="within selected radius"
                slotProps={{
                  primary: {
                    color: 'textPrimary',
                  },
                }}
              />
            </ListItem>
          </Grid>
        )}

        {isLoading && (
          <Grid item size={12}>
            <ListItem dense>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <CircularProgress size={16} />
              </ListItemIcon>
              <ListItemText
                primary="Fetching data..."
                slotProps={{
                  primary: { variant: 'caption', color: 'textSecondary' },
                }}
              />
            </ListItem>
          </Grid>
        )}

        {weekDays.map(day => {
          const handleDayChange = handleToggleFactory(day.day)
          return (
            <Grid item size={6} key={day.day}>
              <ListItemButton onClick={handleDayChange} dense>
                <ListItemIcon
                  sx={{
                    minWidth: 'unset',
                    pr: 1,
                  }}
                >
                  <Stack direction="row" alignItems="center" justifyContent="center">
                    <HHFormCheckboxField sx={{ p: 0.5 }} size="small" name={`selectedDays.${day.day}`} control={control} />
                    <Box width={15} height={15} bgcolor={day.color} />
                  </Stack>
                </ListItemIcon>
                <ListItemText
                  primary={`${day.day?.charAt(0)} - ${day.day?.substring(0, 3)}`}
                  secondary={(() => {
                    const stopsCount = getServiceCountForDay(day.day)
                    return (
                      <>
                        <HHDisplayMoney value={stopsCount} prefix="" formatToDollars={false} decimalScale={0} />
                        <>{` services`}</>
                      </>
                    )
                  })()}
                  slotProps={{
                    primary: {
                      variant: 'caption',
                      color: 'textPrimary',
                    },
                    secondary: {
                      variant: 'caption',
                      color: 'textSecondary',
                    },
                  }}
                />
              </ListItemButton>
            </Grid>
          )
        })}
      </Grid>
    </Card>
  )
}

DaysOfWeekLegends.propTypes = {
  // No props needed as we're using Redux store
}

export default DaysOfWeekLegends
